import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Footer } from './Footer';
import { IslamicChat } from './IslamicChat';
import { TopBar } from './TopBar';

const SECTION_BADGES: Record<'VCN' | 'HDR' | 'SANTE', { label: string; icon: string; className: string }> = {
  VCN: { label: 'VCN', icon: '🎓', className: 'bg-blue-100 text-blue-800' },
  HDR: { label: 'HDR', icon: '🏛️', className: 'bg-purple-100 text-purple-800' },
  SANTE: { label: 'SANTÉ', icon: '🩺', className: 'bg-emerald-100 text-emerald-800' },
};

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navLinks = [
    { to: '/', label: 'Accueil', icon: '' },
    { to: '/exposes', label: 'Exposés', icon: '' },
    { to: '/ramadan', label: 'Ramadan', icon: '' },
    { to: '/events', label: 'Événements', icon: '' },
    { to: '/daara', label: 'Daara', icon: '' },
    { to: '/galerie', label: 'Galerie', icon: '' },
    { to: '/bibliotheque', label: 'Bibliothèque', icon: '' },
    { to: '/mediatheque', label: 'Médiathèque', icon: '' },
    { to: '/faq', label: 'Q/R', icon: '' },
    { to: '/contact', label: 'Contact', icon: '' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex flex-col">
      {/* TopBar - Membre le plus actif */}
      <TopBar />
      
      {/* Spacer pour compenser la TopBar fixe (50px de hauteur) */}
      <div className="h-[50px]"></div>
      
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-md shadow-lg sticky top-[50px] z-50 border-b border-green-100">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="group flex items-center ml-4">
              <div className="w-20 h-20 rounded-full border-[3px] border-green-600 shadow-xl overflow-hidden transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 bg-white p-1.5">
                <img
                  src="/logo.jpg"
                  alt="Hanafia Logo"
                  className="w-full h-full rounded-full object-contain"
                  style={{ objectPosition: 'center center' }}
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-2 bg-green-50 rounded-full px-2 py-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-4 py-2 rounded-full font-medium transition-all duration-300 flex items-center ${
                    isActive(link.to)
                      ? 'bg-green-600 text-white shadow-lg transform scale-105'
                      : 'text-gray-700 hover:bg-white hover:text-green-700'
                  }`}
                >
                  <span className="text-sm">{link.label}</span>
                </Link>
              ))}
            </div>

            {/* User Actions */}
            <div className="hidden lg:flex items-center space-x-3">
              {user ? (
                <>
                  <Link
                    to="/profile"
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-green-50 transition-colors group"
                  >
                    <div className="w-9 h-9 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center text-white font-bold shadow-md group-hover:scale-110 transition-transform text-sm">
                      {user.first_name?.[0] || user.username[0].toUpperCase()}
                    </div>
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-700 group-hover:text-green-700 text-sm">
                        {user.first_name || user.username}
                      </span>
                      {user.section && (
                        <span
                          className={`mt-0.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                            SECTION_BADGES[(user.section as 'VCN' | 'HDR' | 'SANTE') || 'VCN'].className
                          }`}
                        >
                          <span>{SECTION_BADGES[(user.section as 'VCN' | 'HDR' | 'SANTE') || 'VCN'].icon}</span>
                          <span>{SECTION_BADGES[(user.section as 'VCN' | 'HDR' | 'SANTE') || 'VCN'].label}</span>
                        </span>
                      )}
                    </div>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="relative bg-red-500 hover:bg-red-600 text-white p-2.5 rounded-lg font-medium transition-all duration-300 transform hover:scale-110 shadow-md group/btn"
                    title="Déconnexion"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span className="absolute right-full mr-2 top-1/2 transform -translate-y-1/2 bg-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300 pointer-events-none shadow-lg">
                      Déconnexion
                    </span>
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-6 py-2 rounded-full font-medium transition-all duration-300 transform hover:scale-105 shadow-lg text-sm"
                >
                  Connexion
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-200 animate-slide-down bg-white">
              <div className="flex flex-col space-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-4 py-3 rounded-lg font-medium transition-all flex items-center space-x-3 ${
                      isActive(link.to)
                        ? 'bg-green-600 text-white shadow-md'
                        : 'text-gray-700 hover:bg-green-50'
                    }`}
                  >
                    <span>{link.label}</span>
                  </Link>
                ))}
                {user ? (
                  <>
                    <Link
                      to="/profile"
                      onClick={() => setMobileMenuOpen(false)}
                      className="px-4 py-3 rounded-lg hover:bg-green-50 flex items-center space-x-3"
                    >
                      <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center text-white font-bold">
                        {user.first_name?.[0] || user.username[0].toUpperCase()}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium">{user.first_name || user.username}</span>
                        {user.section && (
                          <span
                            className={`mt-0.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                              SECTION_BADGES[(user.section as 'VCN' | 'HDR' | 'SANTE') || 'VCN'].className
                            }`}
                          >
                            <span>{SECTION_BADGES[(user.section as 'VCN' | 'HDR' | 'SANTE') || 'VCN'].icon}</span>
                            <span>{SECTION_BADGES[(user.section as 'VCN' | 'HDR' | 'SANTE') || 'VCN'].label}</span>
                          </span>
                        )}
                      </div>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-3 rounded-lg font-medium text-left flex items-center space-x-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span>Déconnexion</span>
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="bg-gradient-to-r from-green-600 to-green-700 text-white px-4 py-3 rounded-lg font-medium text-center"
                  >
                    Connexion
                  </Link>
                )}
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <Footer />

      {/* HanafiyaAI - Visible uniquement sur les pages utilisateurs */}
      <IslamicChat />
    </div>
  );
};
